import { createContext, useEffect } from "react";
import { usePersistTheme } from "../hooks";
import config from "../config/config";
import { themeProviderPropTypes } from "../propTypes";

/**
 * ThemeContext - Provides theme state and setters throughout the application
 * Manages light/dark theme, sidebar theme, and top menu theme preferences
 * @type {React.Context}
 * @property {string} theme - Current theme ("light" or "dark")
 * @property {Function} setTheme - Updates main theme
 * @property {string} sidebarTheme - Current sidebar theme
 * @property {Function} setSidebarTheme - Updates sidebar theme
 * @property {string} topMenuTheme - Current top menu theme
 * @property {Function} setTopMenuTheme - Updates top menu theme
 */
export const ThemeContext = createContext({
  theme: config.theme.default,
  setTheme: () => {},
  sidebarTheme: config.theme.sidebar.default,
  setSidebarTheme: () => {},
  topMenuTheme: config.theme.header.default,
  setTopMenuTheme: () => {},
});

/**
 * ThemeProvider Component - Wraps application to provide theme context
 * Manages theme persistence across sessions and applies CSS variables based on theme
 *
 * @component
 * @param {React.ReactNode} props.children - Child components to wrap
 * @returns {JSX.Element} Provider component wrapping children
 */
const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = usePersistTheme("theme", config.theme.default);
  const [sidebarTheme, setSidebarTheme] = usePersistTheme("sidebarTheme", config.theme.sidebar.default);
  const [topMenuTheme, setTopMenuTheme] = usePersistTheme("topMenuTheme", config.theme.header.default);

  // Apply CSS variables based on current theme for consistent styling
  useEffect(() => {
    try {
      if (theme === "dark") {
        document.documentElement.style.setProperty("--m-card-bg", "#293042");
        document.documentElement.style.setProperty("--m-text-color", "#fff");
        document.documentElement.style.setProperty("--m-page-bg", "#0f1524");
      } else {
        document.documentElement.style.setProperty("--m-card-bg", "#fff");
        document.documentElement.style.setProperty("--m-text-color", "#111");
        document.documentElement.style.setProperty("--m-page-bg", "#f5f7fb");
      }
    } catch (error) {
      console.error("Failed to apply theme:", error);
    }
  }, [theme]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        setTheme,
        sidebarTheme,
        setSidebarTheme,
        topMenuTheme,
        setTopMenuTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

ThemeProvider.propTypes = themeProviderPropTypes;

export default ThemeProvider;
